// D1 Storage Client — row-level CRUD replacing KV blob storage
// All operations go through /api/d1/{table} endpoints

const D1_API = '/api/d1';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = any;

// ---- Case conversion ----

export function camelToSnake(s: string): string {
  return s.replace(/([A-Z])/g, '_$1').toLowerCase();
}

export function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

// ---- Core CRUD ----

/** List rows from a D1 table, with optional camelCase filters */
export async function d1List<T>(
  table: string,
  filters?: Record<string, string | number | boolean>,
): Promise<T[]> {
  let url = `${D1_API}/${table}`;
  if (filters) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      params.set(k, String(v));
    }
    url += `?${params}`;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`d1List ${table} failed: ${res.status}`);
  return res.json();
}

/** Get a single row by primary key */
export async function d1Get<T>(table: string, id: string | number): Promise<T | null> {
  const res = await fetch(`${D1_API}/${table}/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`d1Get ${table}/${id} failed: ${res.status}`);
  return res.json();
}

/** Upsert (INSERT OR REPLACE) a single row */
export async function d1Upsert(table: string, record: AnyRecord): Promise<void> {
  const res = await fetch(`${D1_API}/${table}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'unknown');
    throw new Error(`d1Upsert ${table} failed: ${res.status} - ${text}`);
  }
}

/** Batch upsert multiple rows */
export async function d1BatchUpsert(table: string, records: AnyRecord[]): Promise<void> {
  if (records.length === 0) return;
  const res = await fetch(`${D1_API}/${table}/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows: records }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'unknown');
    throw new Error(`d1BatchUpsert ${table} failed: ${res.status} - ${text}`);
  }
}

/** Delete a single row by primary key */
export async function d1Delete(table: string, id: string | number): Promise<void> {
  const res = await fetch(`${D1_API}/${table}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`d1Delete ${table}/${id} failed: ${res.status}`);
}

/** Delete rows matching filters */
export async function d1DeleteWhere(table: string, filters: Record<string, string | number | boolean>): Promise<void> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    params.set(k, String(v));
  }
  const res = await fetch(`${D1_API}/${table}?${params}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`d1DeleteWhere ${table} failed: ${res.status}`);
}

// ---- Diff-based sync engine ----

/**
 * Sync a frontend array to D1 by diffing previous vs. next state.
 * Only rows that were added, changed, or removed get written.
 */
export async function d1Sync<T>(
  table: string,
  prev: T[],
  next: T[],
  idField: string = 'id',
): Promise<void> {
  const getId = (item: T) => String((item as AnyRecord)[idField] ?? '');

  const prevMap = new Map<string, T>();
  for (const item of prev) {
    const key = getId(item);
    if (key) prevMap.set(key, item);
  }

  const nextMap = new Map<string, T>();
  for (const item of next) {
    const key = getId(item);
    if (key) nextMap.set(key, item);
  }

  const toUpsert: T[] = [];
  const toDelete: string[] = [];

  // Added or changed
  for (const [key, item] of nextMap) {
    const prevItem = prevMap.get(key);
    if (!prevItem || JSON.stringify(prevItem) !== JSON.stringify(item)) {
      toUpsert.push(item);
    }
  }

  // Removed
  for (const key of prevMap.keys()) {
    if (!nextMap.has(key)) {
      toDelete.push(key);
    }
  }

  // Apply changes
  const promises: Promise<void>[] = [];

  if (toUpsert.length > 0) {
    promises.push(d1BatchUpsert(table, toUpsert as AnyRecord[]));
  }

  for (const id of toDelete) {
    promises.push(d1Delete(table, id));
  }

  await Promise.all(promises);
}

/**
 * Sync a parent table with a nested child array.
 * e.g., clips (parent) with clip.reviews[] (children in clip_reviews table)
 */
export async function d1SyncWithChildren<P>(
  parentTable: string,
  childTable: string,
  prev: P[],
  next: P[],
  parentIdField: string,
  childArrayField: string,
  childForeignKey: string,
): Promise<void> {
  const getField = (obj: P, field: string) => (obj as AnyRecord)[field];

  // Extract children from parents, sync parents without the child array
  const stripChildren = (items: P[]) => items.map(p => {
    const copy = { ...p } as AnyRecord;
    delete copy[childArrayField];
    return copy as P;
  });

  await d1Sync(parentTable, stripChildren(prev), stripChildren(next), parentIdField);

  // For each parent that changed, sync its children
  const prevMap = new Map(prev.map(p => [String(getField(p, parentIdField)), p]));

  for (const parent of next) {
    const parentId = String(getField(parent, parentIdField));
    const prevParent = prevMap.get(parentId);
    const prevChildren = (prevParent ? getField(prevParent, childArrayField) as AnyRecord[] | undefined : undefined) || [];
    const nextChildren = (getField(parent, childArrayField) as AnyRecord[] | undefined) || [];

    if (JSON.stringify(prevChildren) !== JSON.stringify(nextChildren)) {
      await d1DeleteWhere(childTable, { [childForeignKey]: parentId });
      if (nextChildren.length > 0) {
        const childrenWithFK = nextChildren.map((c: AnyRecord) => ({
          ...c,
          [childForeignKey]: parentId,
        }));
        await d1BatchUpsert(childTable, childrenWithFK);
      }
    }
  }

  // Handle deleted parents: clean up their children
  const nextIds = new Set(next.map(p => String(getField(p, parentIdField))));
  for (const p of prev) {
    const id = String(getField(p, parentIdField));
    if (!nextIds.has(id)) {
      await d1DeleteWhere(childTable, { [childForeignKey]: id });
    }
  }
}

/**
 * Append-only sync for tables without stable IDs (e.g., clip_views).
 */
export async function d1AppendOnly<T>(
  table: string,
  prev: T[],
  next: T[],
): Promise<void> {
  if (next.length <= prev.length) return;
  const newItems = next.slice(prev.length);
  if (newItems.length > 0) {
    await d1BatchUpsert(table, newItems as AnyRecord[]);
  }
}
